import { FunctionComponent, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChartWithBars, ChartWithBarsWrapper } from 'PDF/components/Charts/ChartWithBars';
import { ChartWithStackedBars, ChartWithStackedBarsWrapper } from 'PDF/components/Charts/ChartWithStackedBars';
import { DonutWithTextWrapper } from 'PDF/components/Charts/DonutWithText';
import { LinesChartWrapper } from 'PDF/components/Charts/LineCharts';
import { MixedChart } from 'PDF/components/Charts/MixedChart/MixedChart';
import { IImageChart } from 'PDF/components/Charts/types';
import { PDF_SECTIONS } from 'PDF/constants/sections';
import { prepareSectionId } from 'PDF/helpers/pdfMapping';
import { useGenerateImageFromCharts } from 'PDF/hooks/useGenerateImageFromCharts';
import { IPdfPageProps } from 'PDF/types/pdf';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlanChapters, getBusinessPlanType } from 'store/useBusinessToolStore/selectors';

import { useGetOperatingExpensesTableHeadings } from 'components/BusinessToolForm/Sections/FinancialPlan/ProfitAndLossProjection/components/FormSections/OperatingExpensesForm/utils/form';
import {
  useGetCurrentAssetsVariants,
  useGetNonCurrentAssetsVariants,
} from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedBalanceSheets/Sections/Assets/utils/form';
import { useGetLiabilityAndEquityTableHeadings } from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedBalanceSheets/Sections/LiabilitiesAndEquity/utils/form';
import { useGetAdjustmentsForLinkedFields } from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/InDirectForm/InDirectFormComponents/Sections/AdjustmentsForForm/utils/form';
import { useGetOperatingAssetsAndLiabilitiesLinkedFields } from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/InDirectForm/InDirectFormComponents/Sections/OperatingAssetsAndLiabilitiesForm/utils/form';
import { useGetOperatingActivitiesRows } from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/utils/form';

import {
  getArrayOfSourcesOfProjectFinancingAndLoanRepaymentSchedule,
  getDonutChartsArray,
  getFundingDirectionsChartData,
  getFundingDirectionsTableData,
  getInvestmentPerformanceRatios,
  getMagazineItems,
  getOtherPerformanceRatios,
  getPersonnelExpensesData,
  getPlannedExpenses,
  getProfitAndLossChartData,
  getProfitAndLossProjectionTableData,
  getProjectedBalanceSheet,
  getProjectedBalanceSheetChartsData,
  getProjectedCashFlowChartData,
  getProjectedCashFlowTableData,
  getRatiosCharts,
  getVariableExpenses,
} from './mapping';

interface IFinancialPlanProps extends IPdfPageProps {
  isExamplePage: boolean;
}

export const FinancialPlan = memo(({ saveSectionProps, sectionPicture: _, isExamplePage }: IFinancialPlanProps) => {
  const [donutChartsFirstImage, setDonutChartsFirstImage] = useState<IImageChart>();
  const [donutChartsSecondImage, setDonutChartsSecondImage] = useState<IImageChart>();
  const [donutChartsThirdImage, setDonutChartsThirdImage] = useState<IImageChart>();
  const [donutChartsFourthImage, setDonutChartsFourthImage] = useState<IImageChart>();
  const { t } = useTranslation();
  const [projectedBalanceSheetAssetsChart, setProjectedBalanceSheetAssetsChart] = useState<IImageChart | null>(null);
  const [projectedBalanceSheetEquityAndLiabilitiesChart, setProjectedBalanceSheetEquityAndLiabilitiesChart] =
    useState<IImageChart | null>(null);

  const [sourceOfProjectFinancingAndLoanRepaymentFirstImage, setSourceOfProjectFinancingAndLoanRepaymentFirstImage] =
    useState<IImageChart>();
  const [sourceOfProjectFinancingAndLoanRepaymentSecondImage, setSourceOfProjectFinancingAndLoanRepaymentSecondImage] =
    useState<IImageChart>();

  const [lineChartsFirstImages, setLineChartsFirstImages] = useState<IImageChart>();
  const [lineChartsSecondImages, setLineChartsSecondImages] = useState<IImageChart>();
  const [lineChartsThirdImages, setLineChartsThirdImages] = useState<IImageChart>();
  const businessPlanChapters = useBusinessToolStore(getBusinessPlanChapters);
  const businessPlanType = useBusinessToolStore(getBusinessPlanType);
  const currentAssetsVariants = useGetCurrentAssetsVariants(isExamplePage);
  const nonCurrentAssetsVariants = useGetNonCurrentAssetsVariants(isExamplePage);
  const { currentLiabilitiesVariants, nonCurrentLiabilitiesVariants, equityVariants } =
    useGetLiabilityAndEquityTableHeadings(isExamplePage);
  const { operatingExpensesLinkedFields } = useGetOperatingExpensesTableHeadings(isExamplePage);
  const adjustmentsForLinkedFields = useGetAdjustmentsForLinkedFields(isExamplePage);
  const operatingAssetsAndLiabilitiesLinkedFields = useGetOperatingAssetsAndLiabilitiesLinkedFields(isExamplePage);
  const magazineItems = getMagazineItems(businessPlanChapters, businessPlanType, isExamplePage);
  const donutChartsArray = getDonutChartsArray(businessPlanChapters, isExamplePage);
  const { plannedExpensesTable, plannedExpensesChart } = getPlannedExpenses(businessPlanChapters, isExamplePage);
  const { variableExpensesTable, variableExpensesChart, costPerUnitChart } = getVariableExpenses(
    businessPlanChapters,
    isExamplePage,
  );
  const personnelExpenses = getPersonnelExpensesData(businessPlanChapters, isExamplePage);
  const arrayOfSourcesOfProjectFinancingAndLoanRepaymentSchedule =
    getArrayOfSourcesOfProjectFinancingAndLoanRepaymentSchedule(businessPlanChapters, isExamplePage);
  const { profitabilityRatios, lineCharts, solvencyRatios } = getRatiosCharts(businessPlanChapters, isExamplePage);
  const projectedBalanceSheet = getProjectedBalanceSheet(
    businessPlanChapters,
    currentAssetsVariants,
    nonCurrentAssetsVariants,
    currentLiabilitiesVariants,
    nonCurrentLiabilitiesVariants,
    equityVariants,
    isExamplePage,
  );
  const { assetsChartData, equityAndLiabilitiesChartData } = getProjectedBalanceSheetChartsData(
    businessPlanChapters,
    isExamplePage,
  );
  const fundingDirectionsTableData = getFundingDirectionsTableData(businessPlanChapters, isExamplePage);
  const fundingDirectionsChartData = getFundingDirectionsChartData(businessPlanChapters, isExamplePage);
  const investmentPerformanceRatios = getInvestmentPerformanceRatios(businessPlanChapters, isExamplePage);
  const otherPerformanceRatios = getOtherPerformanceRatios(businessPlanChapters, businessPlanType, isExamplePage);
  const operatingActivitiesRowsValues = useGetOperatingActivitiesRows(isExamplePage);

  const projectedCashFlowTable = getProjectedCashFlowTableData(
    businessPlanChapters,
    adjustmentsForLinkedFields,
    operatingAssetsAndLiabilitiesLinkedFields,
    operatingActivitiesRowsValues,
    isExamplePage,
  );
  const projectedCashFlowChartData = getProjectedCashFlowChartData(businessPlanChapters, isExamplePage);
  const profitAndLossTableData = getProfitAndLossProjectionTableData(
    businessPlanChapters,
    operatingExpensesLinkedFields,
    isExamplePage,
  );
  const profitAndLossChartData = getProfitAndLossChartData(businessPlanChapters, isExamplePage);

  const [plannedExpensesChartPortal, plannedExpensesChartImageSrc] = useGenerateImageFromCharts(
    ChartWithStackedBars as FunctionComponent,
    { info: plannedExpensesChart, withBarTopLabels: true, styles: { legendAlignment: 'start', barPercentage: 0.65 } },
  );

  const [profitAndLossChartPortal, profitAndLossChartImageSrc] = useGenerateImageFromCharts(
    ChartWithBars as FunctionComponent,
    {
      info: profitAndLossChartData,
      withDataLabeInsideBar: true,
    },
  );

  const [projectedCashFlowChartPortal, projectedCashFlowChartImageSrc] = useGenerateImageFromCharts(
    MixedChart as FunctionComponent,
    { info: projectedCashFlowChartData, withBarTopLabels: true },
  );
  const [costPerUnitChartPortal, costPerUnitChartImageSrc] = useGenerateImageFromCharts(
    ChartWithStackedBars as FunctionComponent,
    { info: costPerUnitChart, withBarTopLabels: true, withLegend: false },
  );
  const [variableExpensesChartPortal, variableExpensesChartImageSrc] = useGenerateImageFromCharts(
    ChartWithStackedBars as FunctionComponent,
    { isSmall: true, info: variableExpensesChart, withBarTopLabels: true },
  );
  const [profitabilityRatiosChartPortal, profitabilityRatiosChartImageSrc] = useGenerateImageFromCharts(
    ChartWithBars as FunctionComponent,
    { info: profitabilityRatios, aspectRatio: 2.5, withDataLabeInsideBar: true },
  );
  const [solvencyRatiosChartPortal, solvencyRatiosChartImageSrc] = useGenerateImageFromCharts(
    ChartWithBars as FunctionComponent,
    { info: solvencyRatios, aspectRatio: 2.5, withoutUnit: true },
  );

  const [fundingDirectionsChartPortal, fundingDirectionsChartImageSrc] = useGenerateImageFromCharts(
    ChartWithStackedBars as FunctionComponent,
    {
      info: fundingDirectionsChartData,
      aspectRatio: 1.6,
      withBarTopLabels: true,
    },
  );

  const updateDonutChartsImages = (index: number, title: string, src: string) => {
    if (index === 0) {
      setDonutChartsFirstImage({ title, src });
    }
    if (index === 1) {
      setDonutChartsSecondImage({ title, src });
    }
    if (index === 2) {
      setDonutChartsThirdImage({ title, src });
    }
    if (index === 3) {
      setDonutChartsFourthImage({ title, src });
    }
  };

  const updateSourceOfProjectFinancingAndLoanRepaymentImages = (index: number, title: string, src: string) => {
    if (index === 0) {
      setSourceOfProjectFinancingAndLoanRepaymentFirstImage({ title, src });
    }
    if (index === 1) {
      setSourceOfProjectFinancingAndLoanRepaymentSecondImage({ title, src });
    }
  };

  const updateLineChartsImages = (index: number, title: string, src: string) => {
    if (index === 0) {
      setLineChartsFirstImages({ title, src });
    }
    if (index === 1) {
      setLineChartsSecondImages({ title, src });
    }
    if (index === 2) {
      setLineChartsThirdImages({ title, src });
    }
  };

  useEffect(() => {
    const initDataTitles = lineCharts.map((chart) => chart?.title);

    if (!initDataTitles.includes('')) {
      setLineChartsFirstImages({ title: '', src: 'emptyChart' });
    }
    if (!initDataTitles.includes(t('pdfPages.mappings.financialPlan.lineCharts.liquidRatios'))) {
      setLineChartsSecondImages({ title: '', src: 'emptyChart' });
    }
    if (!initDataTitles.includes(t('pdfPages.mappings.financialPlan.lineCharts.efficiencyRatios'))) {
      setLineChartsThirdImages({ title: '', src: 'emptyChart' });
    }
  }, []);

  const updateProjectedBalanceSheetAssetsChartData = (title: string, src: string) => {
    setProjectedBalanceSheetAssetsChart({ title, src });
  };

  const updateProjectedBalanceSheetEquityAndLiabilitiesCharts = (title: string, src: string) => {
    setProjectedBalanceSheetEquityAndLiabilitiesChart({ title, src });
  };

  useEffect(() => {
    if (!assetsChartData.data.some((dataset) => dataset.some(Boolean))) {
      setProjectedBalanceSheetAssetsChart((currentChart) =>
        currentChart?.src === 'emptyChart' ? currentChart : { title: '', src: 'emptyChart' },
      );
    }

    if (!equityAndLiabilitiesChartData.data.some((dataset) => dataset.some(Boolean))) {
      setProjectedBalanceSheetEquityAndLiabilitiesChart((currentChart) =>
        currentChart?.src === 'emptyChart' ? currentChart : { title: '', src: 'emptyChart' },
      );
    }
  }, [assetsChartData, equityAndLiabilitiesChartData]);

  useEffect(() => {
    if (
      plannedExpensesChartImageSrc &&
      profitAndLossChartImageSrc &&
      projectedCashFlowChartImageSrc &&
      costPerUnitChartImageSrc &&
      variableExpensesChartImageSrc &&
      profitabilityRatiosChartImageSrc &&
      solvencyRatiosChartImageSrc &&
      fundingDirectionsChartImageSrc &&
      !!donutChartsFirstImage &&
      !!donutChartsSecondImage &&
      !!donutChartsThirdImage &&
      !!donutChartsFourthImage &&
      !!sourceOfProjectFinancingAndLoanRepaymentFirstImage &&
      !!sourceOfProjectFinancingAndLoanRepaymentSecondImage &&
      !!lineChartsFirstImages?.src &&
      !!lineChartsSecondImages?.src &&
      !!lineChartsThirdImages?.src &&
      projectedBalanceSheetAssetsChart &&
      projectedBalanceSheetEquityAndLiabilitiesChart
    ) {
      const data = {
        plannedExpensesTable,
        plannedExpensesChartImage: { title: '', src: plannedExpensesChartImageSrc },
        costPerUnitChartImage: {
          title: isExamplePage ? 'The Initial Cost per Unit of Product/Service' : t('pdfPages.mappings.financialPlan.initialCostPerUnit'),
          src: costPerUnitChartImageSrc,
        },
        variableExpensesTable,
        variableExpensesChartImage: { title: '', src: variableExpensesChartImageSrc },
        donutChartsImages: [
          donutChartsFirstImage,
          donutChartsSecondImage,
          donutChartsThirdImage,
          donutChartsFourthImage,
        ],
        personnelExpenses,
        sourcesOfProjectFinancingAndloanRepaymentChartImages: [
          sourceOfProjectFinancingAndLoanRepaymentFirstImage,
          sourceOfProjectFinancingAndLoanRepaymentSecondImage,
        ],
        profitabilityRatiosChartImage: {
          title:isExamplePage ? 'Profitability Ratios' : t('pdfPages.mappings.financialPlan.lineCharts.profitRatios'),
          src: profitabilityRatiosChartImageSrc,
        },
        lineChartsImages: [lineChartsFirstImages, lineChartsSecondImages, lineChartsThirdImages],
        solvencyRatiosChartImage: {
          title:isExamplePage ? 'Solvency Ratios' : t('pdfPages.mappings.financialPlan.lineCharts.solvencyRatios'),
          src: solvencyRatiosChartImageSrc,
        },
        projectedBalanceSheetAssetsChart,
        projectedBalanceSheetEquityAndLiabilitiesChart,
        projectedBalanceSheet,
        fundingDirectionsTableData,
        fundingDirectionsChartImage: {
          title: '',
          src: fundingDirectionsChartImageSrc,
        },
        investmentPerformanceRatios,
        otherPerformanceRatios,
        projectedCashFlowTable,
        projectedCashFlowChartImage: {
          title: '',
          src: projectedCashFlowChartImageSrc,
        },
        profitAndLossTableData,
        profitAndLossChart: {
          title: '',
          src: profitAndLossChartImageSrc,
        },
      };

      saveSectionProps(prepareSectionId(businessPlanType, PDF_SECTIONS.FINANCIAL_PLAN), {
        magazineItems,
        data,
      });
    }
  }, [
    lineChartsFirstImages,
    lineChartsThirdImages,
    lineChartsSecondImages,
    donutChartsFirstImage,
    donutChartsSecondImage,
    donutChartsThirdImage,
    donutChartsFourthImage,
    profitabilityRatiosChartImageSrc,
    plannedExpensesChartImageSrc,
    profitAndLossChartImageSrc,
    projectedCashFlowChartImageSrc,
    costPerUnitChartImageSrc,
    variableExpensesChartImageSrc,
    donutChartsArray,
    sourceOfProjectFinancingAndLoanRepaymentFirstImage,
    sourceOfProjectFinancingAndLoanRepaymentSecondImage,
    arrayOfSourcesOfProjectFinancingAndLoanRepaymentSchedule,
    lineCharts,
    solvencyRatiosChartImageSrc,
    projectedBalanceSheetAssetsChart,
    projectedBalanceSheetEquityAndLiabilitiesChart,
    fundingDirectionsChartImageSrc,
  ]);

  return (
    <>
      {plannedExpensesChartPortal}
      {profitAndLossChartPortal}
      {costPerUnitChartPortal}
      {variableExpensesChartPortal}
      {fundingDirectionsChartPortal}
      {projectedCashFlowChartPortal}
      <ChartWithBarsWrapper aspectRatio={2.5} info={profitAndLossChartData} updateState={() => undefined} />
      {donutChartsArray.map((item, index) => (
        <DonutWithTextWrapper
          key={index}
          info={item}
          updateState={(title, src) => updateDonutChartsImages(index, title, src)}
          labelTextWidthDivider={!index ? 10 : 2}
        />
      ))}
      {arrayOfSourcesOfProjectFinancingAndLoanRepaymentSchedule.map((item, index) => (
        <ChartWithStackedBarsWrapper
          key={index}
          info={item}
          aspectRatio={1.7}
          withBarTopLabels={index === 0}
          withBarInsideLabels
          updateState={(title, src) => updateSourceOfProjectFinancingAndLoanRepaymentImages(index, title, src)}
        />
      ))}
      {profitabilityRatiosChartPortal}
      {lineCharts.map((item, index) => (
        <LinesChartWrapper
          key={index}
          info={item}
          aspectRatio={2.5}
          updateState={(title, src) => updateLineChartsImages(index, title, src)}
        />
      ))}
      <ChartWithBarsWrapper
        aspectRatio={2.5}
        info={assetsChartData}
        updateState={updateProjectedBalanceSheetAssetsChartData}
      />
      <ChartWithBarsWrapper
        aspectRatio={2.5}
        info={equityAndLiabilitiesChartData}
        updateState={updateProjectedBalanceSheetEquityAndLiabilitiesCharts}
      />
      {solvencyRatiosChartPortal}
    </>
  );
});
